import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getClassroomDetail, createClassroom, updateClassroom, getBranchOptions } from '../api/classroomApi';
import { appToast } from '../../../../components/AppToast';

export function useClassroomEntryScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingClassroomId = searchParams.get('id');

  const [form, setForm] = useState({
    name: '',
    branch_id: '',
  });

  const [branchOptions, setBranchOptions] = useState([]);
  const [operationalStatus, setOperationalStatus] = useState('active');
  const [isLoading, setIsLoading] = useState(Boolean(editingClassroomId));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load branch dropdown options
  useEffect(() => {
    async function loadBranches() {
      try {
        const rawBranches = await getBranchOptions();
        const formatted = (rawBranches || []).map((b) => ({
          value: String(b.id || b.value),
          label: b.name || b.label || `Branch #${b.id}`,
        }));
        setBranchOptions(formatted);

        // Default to first branch if creating new
        if (!editingClassroomId && formatted.length > 0) {
          setForm((prev) => (prev.branch_id ? prev : { ...prev, branch_id: formatted[0].value }));
        }
      } catch (err) {
        console.error('Failed to load branch options:', err);
      }
    }
    loadBranches();
  }, [editingClassroomId]);

  // Load existing classroom if editing
  useEffect(() => {
    async function loadIfEditing() {
      if (!editingClassroomId) return;
      setIsLoading(true);
      setError('');
      try {
        const detail = await getClassroomDetail(editingClassroomId);
        if (detail) {
          setForm({
            name: detail.name || '',
            branch_id: detail.branch_id ? String(detail.branch_id) : '',
          });
          setOperationalStatus(detail.status === 'Active' ? 'active' : 'inactive');
        } else {
          setError('Classroom not found.');
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Failed to load classroom details.';
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      } finally {
        setIsLoading(false);
      }
    }
    loadIfEditing();
  }, [editingClassroomId]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Classroom Name is required.');
      appToast.error('Classroom Name is required.');
      return;
    }
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: form.name.trim(),
        branch_id: form.branch_id ? parseInt(form.branch_id, 10) : null,
        status: operationalStatus === 'active' ? 'Active' : 'Archive',
      };

      if (editingClassroomId) {
        await updateClassroom(editingClassroomId, payload);
        appToast.success('Classroom updated successfully.');
        setSuccess('Classroom updated successfully.');
      } else {
        await createClassroom(payload);
        appToast.success('Classroom created successfully.');
        setSuccess('Classroom created successfully.');
      }

      setTimeout(() => {
        navigate('/masters/classroom/list');
      }, 600);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to save classroom.';
      const errStr = typeof msg === 'string' ? msg : JSON.stringify(msg);
      setError(errStr);
      appToast.error(errStr);
    } finally {
      setIsSaving(false);
    }
  };

  const completionPercent = useMemo(() => {
    const fields = ['name', 'branch_id'];
    const filled = fields.filter((f) => String(form[f] || '').trim().length > 0).length;
    return Math.round((filled / fields.length) * 100);
  }, [form]);

  return {
    state: {
      form,
      operationalStatus,
      isLoading,
      isSaving,
      error,
      success,
      isEditMode: Boolean(editingClassroomId),
      completionPercent,
      branchOptions,
    },
    actions: {
      patchForm,
      setOperationalStatus,
      handleSave,
    },
  };
}
